/**
 * Desktop App settings section.
 *
 * Displays registered Ahoy Desktop devices and lets users revoke them.
 * Gated by the `ahoy_desktop_app` feature flag.
 */

import {
  CheckCircle,
  Laptop01,
  Monitor04,
  Trash01,
  XCircle,
} from '@untitledui/icons';
import { type ReactNode, useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useOrg } from '@/contexts/OrgContext';
import {
  type RecallDevice,
  useRecallDevices,
  useRevokeDevice,
} from '@/hooks/integrations/recall/useRecallDevices';
import { Button } from '@/lib/base/buttons/button';
import { ConfirmationDialog } from '@/lib/base/overlays/confirmation-dialog';
import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { QueryErrorState } from '@/lib/components/QueryErrorState';
import { formatRelativeTime } from '@/lib/utils/timeFormatting';
import { useViewHealthMarker } from '@/lib/view-health';
import { cx } from '@/utils/cx';

// ---------------------------------------------------------------------------
// Module-level constants
// ---------------------------------------------------------------------------

const INACTIVE_THRESHOLD_MS = 14 * 24 * 60 * 60 * 1000;

const PERMISSION_KEYS = [
  'microphone',
  'system_audio',
  'accessibility',
] as const;

type PermissionKey = (typeof PERMISSION_KEYS)[number];

const PERMISSION_LABELS: Record<PermissionKey, string> = {
  microphone: 'Microphone',
  system_audio: 'System audio',
  accessibility: 'Accessibility',
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PermissionPill({
  permissionKey,
  granted,
}: {
  permissionKey: PermissionKey;
  granted: boolean;
}) {
  const Icon = granted ? CheckCircle : XCircle;
  const colorClass = granted
    ? 'text-utility-success-600'
    : 'text-utility-error-600';

  return (
    <span
      className='inline-flex items-center gap-1 rounded-full border border-secondary px-2 py-0.5 text-xs'
      aria-label={`${PERMISSION_LABELS[permissionKey]} permission: ${granted ? 'granted' : 'denied'}`}
    >
      <Icon className={cx('h-3 w-3', colorClass)} />
      {PERMISSION_LABELS[permissionKey]}
    </span>
  );
}

function DesktopDeviceCard({
  device,
  onRevokeClick,
}: {
  device: RecallDevice;
  onRevokeClick: (device: RecallDevice) => void;
}) {
  const handleClick = useCallback(() => {
    onRevokeClick(device);
  }, [device, onRevokeClick]);

  const PlatformIcon = device.platform === 'mac' ? Laptop01 : Monitor04;

  const isInactive =
    device.last_seen_at !== null &&
    Date.now() - new Date(device.last_seen_at).getTime() >
      INACTIVE_THRESHOLD_MS;

  return (
    <div
      className={cx(
        'rounded-lg border border-secondary p-4',
        isInactive && 'opacity-60'
      )}
    >
      <div className='flex items-start justify-between gap-3'>
        <div className='flex items-center gap-2'>
          <PlatformIcon className='h-4 w-4 flex-shrink-0 text-fg-tertiary' />
          <span className='text-sm font-medium text-fg-primary'>
            {device.device_name}
          </span>
          {isInactive && (
            <span className='rounded-full bg-bg-tertiary px-2 py-0.5 text-xs text-fg-tertiary'>
              Inactive
            </span>
          )}
        </div>
        <Button
          size='sm'
          color='tertiary-destructive'
          iconLeading={Trash01}
          aria-label={`Revoke ${device.device_name}`}
          onClick={handleClick}
        />
      </div>
      <div className='mt-2 flex items-center gap-3 text-xs text-fg-tertiary'>
        <span>Version {device.app_version ?? '—'}</span>
        <span>·</span>
        <span>
          Last seen{' '}
          {device.last_seen_at
            ? formatRelativeTime(device.last_seen_at)
            : 'Never'}
        </span>
      </div>
      <div className='mt-3 flex flex-wrap gap-1.5'>
        {PERMISSION_KEYS.map(key => (
          <PermissionPill
            key={key}
            permissionKey={key}
            granted={device.permissions[key]}
          />
        ))}
      </div>
    </div>
  );
}

function DesktopAppEmptyState() {
  return (
    <div className='rounded-lg border border-secondary p-8 text-center'>
      <h3 className='text-sm font-semibold text-fg-primary'>
        No desktop devices connected yet
      </h3>
      <p className='mt-1 text-sm text-fg-tertiary'>
        Install Ahoy Desktop to record meetings from your computer.
      </p>
      <Button href='/desktop/install'>Download Ahoy Desktop</Button>
    </div>
  );
}

function DesktopDeviceList({
  devices,
  onRevokeClick,
}: {
  devices: RecallDevice[];
  onRevokeClick: (device: RecallDevice) => void;
}) {
  return (
    <div className='flex flex-col gap-3'>
      {devices.map(device => (
        <DesktopDeviceCard
          key={device.device_id}
          device={device}
          onRevokeClick={onRevokeClick}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top-level section export
// ---------------------------------------------------------------------------

export function DesktopAppSection() {
  useViewHealthMarker('settings-section-ready');

  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;

  const devicesQuery = useRecallDevices(orgId);
  const revokeMutation = useRevokeDevice(orgId);

  const [deviceToRevoke, setDeviceToRevoke] = useState<RecallDevice | null>(
    null
  );

  const handleRevokeClick = useCallback((device: RecallDevice) => {
    setDeviceToRevoke(device);
  }, []);

  const handleCancelRevoke = useCallback(() => {
    setDeviceToRevoke(null);
  }, []);

  const handleConfirmRevoke = useCallback(async () => {
    if (!deviceToRevoke) {
      return;
    }
    try {
      await revokeMutation.mutateAsync(deviceToRevoke.device_id);
      toast.success('Device revoked');
    } catch {
      toast.error("We couldn't revoke the device. Try again.");
    } finally {
      setDeviceToRevoke(null);
    }
  }, [deviceToRevoke, revokeMutation]);

  let body: ReactNode;

  if (devicesQuery.isLoading) {
    body = (
      <div className='flex flex-col gap-3'>
        <Skeleton className='h-24 w-full rounded-lg' />
        <Skeleton className='h-24 w-full rounded-lg' />
        <Skeleton className='h-24 w-full rounded-lg' />
      </div>
    );
  } else if (devicesQuery.isError) {
    body = (
      <QueryErrorState
        error={devicesQuery.error}
        label="We couldn't load your devices"
        onRetry={devicesQuery.refetch}
        size='sm'
      />
    );
  } else if (devicesQuery.data && devicesQuery.data.length === 0) {
    body = <DesktopAppEmptyState />;
  } else if (devicesQuery.data && devicesQuery.data.length > 0) {
    body = (
      <DesktopDeviceList
        devices={devicesQuery.data}
        onRevokeClick={handleRevokeClick}
      />
    );
  } else {
    body = null;
  }

  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>Ahoy Desktop</h2>
        <p className='mt-0.5 text-sm text-fg-tertiary'>
          Manage your Ahoy Desktop installs. Sign out devices you no longer use.
        </p>
      </div>
      <div>{body}</div>
      <ConfirmationDialog
        isOpen={deviceToRevoke !== null}
        title={
          deviceToRevoke
            ? `Revoke ${deviceToRevoke.device_name}?`
            : 'Revoke device?'
        }
        description='This signs the device out immediately. The user will need to sign back in to record meetings on it.'
        confirmLabel='Revoke'
        cancelLabel='Cancel'
        variant='destructive'
        isLoading={revokeMutation.isPending}
        onConfirm={handleConfirmRevoke}
        onCancel={handleCancelRevoke}
        onClose={handleCancelRevoke}
      />
    </div>
  );
}
